// DASS-21 Mini Questionnaire Data
// 7 carefully selected questions covering Depression, Anxiety, and Stress
// Based on the DASS-21 clinical assessment tool

const dassQuestions = {
  // ===== DEPRESSION (3 questions) =====
  depression: [
    {
      id: 'd1',
      question: "I couldn't seem to experience any positive feeling at all",
      dimension: 'depression',
      answers: [
        { text: "Did not apply to me at all", score: 0 },
        { text: "Applied to me to some degree, or some of the time", score: 1 },
        { text: "Applied to me to a considerable degree, or a good part of time", score: 2 },
        { text: "Applied to me very much, or most of the time", score: 3 }
      ]
    },
    {
      id: 'd2',
      question: "I felt that I had nothing to look forward to",
      dimension: 'depression',
      answers: [
        { text: "Did not apply to me at all", score: 0 },
        { text: "Applied to me to some degree, or some of the time", score: 1 },
        { text: "Applied to me to a considerable degree, or a good part of time", score: 2 },
        { text: "Applied to me very much, or most of the time", score: 3 }
      ]
    },
    {
      id: 'd3',
      question: "I felt down-hearted and blue",
      dimension: 'depression',
      answers: [
        { text: "Did not apply to me at all", score: 0 },
        { text: "Applied to me to some degree, or some of the time", score: 1 },
        { text: "Applied to me to a considerable degree, or a good part of time", score: 2 },
        { text: "Applied to me very much, or most of the time", score: 3 }
      ]
    }
  ],

  // ===== ANXIETY (2 questions) =====
  anxiety: [
    {
      id: 'a1',
      question: "I was aware of dryness of my mouth",
      dimension: 'anxiety',
      answers: [
        { text: "Did not apply to me at all", score: 0 },
        { text: "Applied to me to some degree, or some of the time", score: 1 },
        { text: "Applied to me to a considerable degree, or a good part of time", score: 2 },
        { text: "Applied to me very much, or most of the time", score: 3 }
      ]
    },
    {
      id: 'a2',
      question: "I experienced trembling (e.g., in the hands)",
      dimension: 'anxiety',
      answers: [
        { text: "Did not apply to me at all", score: 0 },
        { text: "Applied to me to some degree, or some of the time", score: 1 },
        { text: "Applied to me to a considerable degree, or a good part of time", score: 2 },
        { text: "Applied to me very much, or most of the time", score: 3 }
      ]
    }
  ],

  // ===== STRESS (2 questions) =====
  stress: [
    {
      id: 's1',
      question: "I found it hard to wind down",
      dimension: 'stress',
      answers: [
        { text: "Did not apply to me at all", score: 0 },
        { text: "Applied to me to some degree, or some of the time", score: 1 },
        { text: "Applied to me to a considerable degree, or a good part of time", score: 2 },
        { text: "Applied to me very much, or most of the time", score: 3 }
      ]
    },
    {
      id: 's2',
      question: "I found it difficult to relax",
      dimension: 'stress',
      answers: [
        { text: "Did not apply to me at all", score: 0 },
        { text: "Applied to me to some degree, or some of the time", score: 1 },
        { text: "Applied to me to a considerable degree, or a good part of time", score: 2 },
        { text: "Applied to me very much, or most of the time", score: 3 }
      ]
    }
  ]
};

// DASS Severity Categories (using DASS-21 scoring thresholds, multiplied by 2)
const dassSeverity = {
  depression: {
    normal: { min: 0, max: 9, label: 'Normal', color: '#10B981', description: 'You\'re experiencing typical emotional responses with no significant depressive symptoms.' },
    mild: { min: 10, max: 13, label: 'Mild', color: '#06B6D4', description: 'You may notice occasional low mood or lack of motivation, but symptoms are manageable.' },
    moderate: { min: 14, max: 20, label: 'Moderate', color: '#F59E0B', description: 'Persistent low mood, reduced interest, and noticeable impact on daily functioning.' },
    severe: { min: 21, max: 27, label: 'Severe', color: '#EF4444', description: 'Significant depressive symptoms affecting work, relationships, and self-care. Professional support recommended.' },
    extremelySevere: { min: 28, max: 999, label: 'Extremely Severe', color: '#991B1B', description: 'Intense, persistent depression severely impacting all life areas. Immediate professional help strongly advised.' }
  },
  anxiety: {
    normal: { min: 0, max: 7, label: 'Normal', color: '#10B981', description: 'You\'re experiencing normal levels of worry without significant anxiety symptoms.' },
    mild: { min: 8, max: 9, label: 'Mild', color: '#06B6D4', description: 'Occasional nervousness or worry, but generally able to manage daily activities.' },
    moderate: { min: 10, max: 14, label: 'Moderate', color: '#F59E0B', description: 'Regular anxiety symptoms including tension, worry, and physical sensations affecting daily life.' },
    severe: { min: 15, max: 19, label: 'Severe', color: '#EF4444', description: 'Frequent anxiety, panic symptoms, and avoidance behaviors significantly disrupting daily functioning.' },
    extremelySevere: { min: 20, max: 999, label: 'Extremely Severe', color: '#991B1B', description: 'Overwhelming anxiety with intense physical symptoms and severe impairment. Professional intervention needed.' }
  },
  stress: {
    normal: { min: 0, max: 14, label: 'Normal', color: '#10B981', description: 'You\'re managing life\'s challenges with typical stress responses and good coping.' },
    mild: { min: 15, max: 18, label: 'Mild', color: '#06B6D4', description: 'Experiencing mild tension and occasional difficulty relaxing, but maintaining overall functioning.' },
    moderate: { min: 19, max: 25, label: 'Moderate', color: '#F59E0B', description: 'Notable stress with irritability, difficulty relaxing, and heightened reactivity to situations.' },
    severe: { min: 26, max: 33, label: 'Severe', color: '#EF4444', description: 'High stress levels with persistent tension, agitation, and difficulty managing daily demands.' },
    extremelySevere: { min: 34, max: 999, label: 'Extremely Severe', color: '#991B1B', description: 'Extreme stress causing exhaustion, overwhelm, and severe difficulty coping. Professional support essential.' }
  }
};

// Helper function to determine severity category
function getDASSSeverity(score, dimension) {
  const categories = dassSeverity[dimension];
  for (const [key, value] of Object.entries(categories)) {
    if (score >= value.min && score <= value.max) {
      return { category: key, ...value };
    }
  }
  return categories.normal;
}

// Helper function to get all DASS questions in sequence
function getAllDASSQuestions() {
  return [
    ...dassQuestions.depression,
    ...dassQuestions.anxiety,
    ...dassQuestions.stress
  ];
}


// ES6 Exports
export { dassQuestions, dassSeverity, getAllDASSQuestions, getDASSSeverity };
